import React, { useState, useEffect } from 'react';
import { ChevronDown, CheckCircle, RefreshCw } from 'lucide-react';
import { getPlayers, createTest, deleteTest, getTests, syncPassingDrillTests, syncIsrtMaxHR, getTestNotes, saveTestNote } from '../api';
import {
  MOBILITY_TESTS, STRENGTH_TESTS, SKINFOLD_SITES,
  num, lsiPct, lsiColor, hqRatio, hqColor, abAdRatio, abAdColor, pctDiff, median, cmjPeakPowerWatts,
} from './physicalTestingShared';

const today = () => new Date().toISOString().split('T')[0];

const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' };

function CollapsibleSection({ title, open, onToggle, children }) {
  return (
    <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', marginBottom: 16, overflow: 'hidden' }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', cursor: 'pointer' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{title}</h3>
        <ChevronDown size={18} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </div>
      {open && <div style={{ padding: '0 20px 20px' }}>{children}</div>}
    </div>
  );
}

function LRRow({ label, left, right, onLeft, onRight, showLsi = true }) {
  const pct = showLsi ? lsiPct(left, right) : null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 80px', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-strong)' }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
      <input type="number" value={left} onChange={e => onLeft(e.target.value)} placeholder="L" style={inputStyle} />
      <input type="number" value={right} onChange={e => onRight(e.target.value)} placeholder="R" style={inputStyle} />
      {showLsi && (
        <div style={{ fontSize: 12, fontWeight: 700, color: lsiColor(pct), textAlign: 'center' }}>
          {pct !== null ? `${pct.toFixed(0)}%` : '—'}
        </div>
      )}
    </div>
  );
}

const emptyMobility = MOBILITY_TESTS.reduce((acc, t) => ({ ...acc, [`${t.key}_left`]: '', [`${t.key}_right`]: '' }), {});
const emptyStrength = STRENGTH_TESTS.reduce((acc, t) => ({ ...acc, [`${t.key}_left`]: '', [`${t.key}_right`]: '' }), {});
const emptySkinfold = SKINFOLD_SITES.reduce((acc, s) => ({ ...acc, [s]: { m1: '', m2: '', m3: '' } }), {});

export default function PhysicalTestInput({ currentUser }) {
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState('');
  const [testDate, setTestDate] = useState(today());
  const [open, setOpen] = useState({ mobility: true, strength: false, performance: false, other: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [isrtSyncing, setIsrtSyncing] = useState(false);
  const [isrtSyncMsg, setIsrtSyncMsg] = useState('');

  const [mobility, setMobility] = useState(emptyMobility);
  const [strength, setStrength] = useState(emptyStrength);
  const [sprint, setSprint] = useState({ sprint_5m: '', sprint_10m: '', sprint_30m: '' });
  const [cmj, setCmj] = useState({ height_cm: '', peak_power_wkg: '' });
  const [hop, setHop] = useState({ mean_rsi: '' });
  const [dropjump, setDropjump] = useState({ rsi: '', peak_power_wkg: '' });
  const [dropjumpSL, setDropjumpSL] = useState({ rsi_left: '', rsi_right: '', peak_power_wkg_left: '', peak_power_wkg_right: '' });
  const [isrt, setIsrt] = useState({ final_stage: '', hr_at_end: '', hr_recovery_30s: '', hr_recovery_60s: '', hr_recovery_90s: '', hr_recovery_120s: '' });
  const [weight, setWeight] = useState({ weight_kg: '' });
  const [skinfold, setSkinfold] = useState(emptySkinfold);
  const [notes, setNotes] = useState('');
  // Which sections already had a saved row for the current player+date — lets handleSaveAll
  // tell "nothing entered yet" apart from "user cleared a section that had data" on save.
  const [existingForDate, setExistingForDate] = useState({});

  useEffect(() => { getPlayers().then(p => setPlayers(p || [])).catch(() => {}); }, []);

  // One-shot hint set by the "Edit" action in the Test History table — preselect player+date then clear it.
  useEffect(() => {
    const raw = localStorage.getItem('edit_test_session');
    if (!raw) return;
    localStorage.removeItem('edit_test_session');
    try {
      const { playerId: pid, date } = JSON.parse(raw);
      if (pid) setPlayerId(String(pid));
      if (date) setTestDate(date);
    } catch {}
  }, []);

  // Pre-fill every section with whatever this player already has on record for this date
  // (so re-opening a date to correct a value shows the existing entry rather than a blank form).
  useEffect(() => {
    if (!playerId || !testDate) return;
    const find = rows => (rows || []).find(r => (r.test_date || '').slice(0, 10) === testDate);
    Promise.all(['mobility', 'strength', 'sprint', 'cmj', 'hop', 'isrt', 'weight', 'skinfold', 'dropjump', 'dropjump_sl'].map(t => getTests(t, playerId)))
      .then(([mob, str, spr, cmjRows, hopRows, isrtRows, weightRows, skinfoldRows, dropjumpRows, dropjumpSLRows]) => {
        const mobRow = find(mob);
        setMobility(mobRow
          ? MOBILITY_TESTS.reduce((acc, t) => ({ ...acc, [`${t.key}_left`]: mobRow[`${t.key}_left`] ?? '', [`${t.key}_right`]: mobRow[`${t.key}_right`] ?? '' }), {})
          : emptyMobility);

        const strRow = find(str);
        setStrength(strRow
          ? STRENGTH_TESTS.reduce((acc, t) => ({ ...acc, [`${t.key}_left`]: strRow[`${t.key}_left`] ?? '', [`${t.key}_right`]: strRow[`${t.key}_right`] ?? '' }), {})
          : emptyStrength);

        const sprRow = find(spr);
        setSprint(sprRow ? { sprint_5m: sprRow.sprint_5m ?? '', sprint_10m: sprRow.sprint_10m ?? '', sprint_30m: sprRow.sprint_30m ?? '' } : { sprint_5m: '', sprint_10m: '', sprint_30m: '' });

        const cmjRow = find(cmjRows);
        setCmj(cmjRow ? { height_cm: cmjRow.height_cm ?? '', peak_power_wkg: cmjRow.peak_power_wkg ?? '' } : { height_cm: '', peak_power_wkg: '' });

        const hopRow = find(hopRows);
        setHop(hopRow ? { mean_rsi: hopRow.mean_rsi ?? '' } : { mean_rsi: '' });

        const dropjumpRow = find(dropjumpRows);
        setDropjump(dropjumpRow ? { rsi: dropjumpRow.rsi ?? '', peak_power_wkg: dropjumpRow.peak_power_wkg ?? '' } : { rsi: '', peak_power_wkg: '' });

        const dropjumpSLRow = find(dropjumpSLRows);
        setDropjumpSL(dropjumpSLRow
          ? { rsi_left: dropjumpSLRow.rsi_left ?? '', rsi_right: dropjumpSLRow.rsi_right ?? '', peak_power_wkg_left: dropjumpSLRow.peak_power_wkg_left ?? '', peak_power_wkg_right: dropjumpSLRow.peak_power_wkg_right ?? '' }
          : { rsi_left: '', rsi_right: '', peak_power_wkg_left: '', peak_power_wkg_right: '' });

        const isrtRow = find(isrtRows);
        setIsrt(isrtRow
          ? { final_stage: isrtRow.final_stage ?? '', hr_at_end: isrtRow.hr_at_end ?? '', hr_recovery_30s: isrtRow.hr_recovery_30s ?? '', hr_recovery_60s: isrtRow.hr_recovery_60s ?? '', hr_recovery_90s: isrtRow.hr_recovery_90s ?? '', hr_recovery_120s: isrtRow.hr_recovery_120s ?? '' }
          : { final_stage: '', hr_at_end: '', hr_recovery_30s: '', hr_recovery_60s: '', hr_recovery_90s: '', hr_recovery_120s: '' });

        const weightRow = find(weightRows);
        setWeight(weightRow ? { weight_kg: weightRow.weight_kg ?? '' } : { weight_kg: '' });

        const skinRow = find(skinfoldRows);
        if (skinRow) {
          const filled = SKINFOLD_SITES.reduce((acc, s) => ({ ...acc, [s]: { m1: '', m2: '', m3: '' } }), {});
          (skinRow.sites || []).forEach(site => {
            if (filled[site.site_name]) filled[site.site_name] = { m1: site.m1 ?? '', m2: site.m2 ?? '', m3: site.m3 ?? '' };
          });
          setSkinfold(filled);
        } else {
          setSkinfold(emptySkinfold);
        }

        setExistingForDate({
          mobility: !!mobRow, strength: !!strRow, sprint: !!sprRow, cmj: !!cmjRow,
          hop: !!hopRow, isrt: !!isrtRow, weight: !!weightRow, skinfold: !!skinRow,
          dropjump: !!dropjumpRow, dropjump_sl: !!dropjumpSLRow,
        });
      })
      .catch(() => {});
  }, [playerId, testDate]);

  // Pre-fill the notes field if this player already has a note for this date
  // (e.g. re-opening the same day's entry to add more results).
  useEffect(() => {
    if (!playerId || !testDate) { setNotes(''); return; }
    getTestNotes(playerId)
      .then(rows => {
        const existing = (rows || []).find(r => (r.test_date || '').slice(0, 10) === testDate);
        setNotes(existing?.notes || '');
      })
      .catch(() => setNotes(''));
  }, [playerId, testDate]);

  const player = players.find(p => String(p.id) === String(playerId));
  const isGK = player?.position === 'GK';

  const toggle = key => setOpen(o => ({ ...o, [key]: !o[key] }));

  const setM = (key, side, val) => setMobility(f => ({ ...f, [`${key}_${side}`]: val }));
  const setS = (key, side, val) => setStrength(f => ({ ...f, [`${key}_${side}`]: val }));

  const hq  = hqRatio(strength);
  const abad = abAdRatio(strength);

  const skinfoldRows = SKINFOLD_SITES.map(site => {
    const { m1, m2, m3 } = skinfold[site];
    const diff = pctDiff(m1, m2);
    const needsM3 = diff !== null && diff > 10;
    let finalVal = null;
    if (num(m1) !== null && num(m2) !== null) {
      finalVal = needsM3 && num(m3) !== null
        ? median([num(m1), num(m2), num(m3)])
        : (needsM3 ? null : (num(m1) + num(m2)) / 2);
    }
    return { site, m1, m2, m3, diff, needsM3, finalVal };
  });
  const sum8 = skinfoldRows.reduce((s, r) => s + (r.finalVal || 0), 0);
  const sum8Complete = skinfoldRows.every(r => r.finalVal !== null);

  const hasAny = obj => Object.values(obj).some(v => v !== '' && v !== null);
// Empty-string inputs must become null before hitting NUMERIC columns — Postgres rejects '' outright.
const cleanNulls = obj => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === '' ? null : v]));

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const res = await syncPassingDrillTests();
      setSyncMsg(`Synced ${res.synced} new test${res.synced === 1 ? '' : 's'}.`);
    } catch {
      setSyncMsg('Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const handleIsrtHrSync = async () => {
    setIsrtSyncing(true);
    setIsrtSyncMsg('');
    try {
      const res = await syncIsrtMaxHR();
      setIsrtSyncMsg(`Filled in HR at end for ${res.updated} test${res.updated === 1 ? '' : 's'}.`);
    } catch {
      setIsrtSyncMsg('Sync failed.');
    } finally {
      setIsrtSyncing(false);
    }
  };

  const handleSaveAll = async () => {
    if (!playerId) { alert('Please select a player first.'); return; }
    setSaving(true);
    try {
      const pid = parseInt(playerId);
      const createdBy = currentUser?.id || null;

      if (hasAny(mobility)) {
        await createTest('mobility', { player_id: pid, test_date: testDate, created_by: createdBy, ...cleanNulls(mobility) });
      } else if (existingForDate.mobility) {
        await deleteTest('mobility', pid, testDate);
      }
      if (hasAny(strength)) {
        await createTest('strength', { player_id: pid, test_date: testDate, created_by: createdBy, ...cleanNulls(strength) });
      } else if (existingForDate.strength) {
        await deleteTest('strength', pid, testDate);
      }
      if (hasAny(sprint)) {
        const payload = cleanNulls(sprint);
        if (!isGK) delete payload.sprint_5m;
        await createTest('sprint', { player_id: pid, test_date: testDate, created_by: createdBy, ...payload });
      } else if (existingForDate.sprint) {
        await deleteTest('sprint', pid, testDate);
      }
      if (hasAny(cmj)) {
        await createTest('cmj', { player_id: pid, test_date: testDate, created_by: createdBy, ...cleanNulls(cmj) });
      } else if (existingForDate.cmj) {
        await deleteTest('cmj', pid, testDate);
      }
      if (hasAny(hop)) {
        await createTest('hop', { player_id: pid, test_date: testDate, created_by: createdBy, ...cleanNulls(hop) });
      } else if (existingForDate.hop) {
        await deleteTest('hop', pid, testDate);
      }
      if (hasAny(dropjump)) {
        await createTest('dropjump', { player_id: pid, test_date: testDate, created_by: createdBy, ...cleanNulls(dropjump) });
      } else if (existingForDate.dropjump) {
        await deleteTest('dropjump', pid, testDate);
      }
      if (hasAny(dropjumpSL)) {
        await createTest('dropjump_sl', { player_id: pid, test_date: testDate, created_by: createdBy, ...cleanNulls(dropjumpSL) });
      } else if (existingForDate.dropjump_sl) {
        await deleteTest('dropjump_sl', pid, testDate);
      }
      if (hasAny(isrt)) {
        await createTest('isrt', { player_id: pid, test_date: testDate, created_by: createdBy, ...cleanNulls(isrt) });
      } else if (existingForDate.isrt) {
        await deleteTest('isrt', pid, testDate);
      }
      if (hasAny(weight)) {
        await createTest('weight', { player_id: pid, test_date: testDate, created_by: createdBy, ...cleanNulls(weight) });
      } else if (existingForDate.weight) {
        await deleteTest('weight', pid, testDate);
      }
      const filledSites = skinfoldRows.filter(r => r.finalVal !== null);
      if (filledSites.length > 0) {
        await createTest('skinfold', {
          player_id: pid, test_date: testDate, created_by: createdBy,
          sites: filledSites.map(r => ({ site_name: r.site, m1: num(r.m1), m2: num(r.m2), m3: num(r.m3), final_value: r.finalVal })),
        });
      } else if (existingForDate.skinfold) {
        await deleteTest('skinfold', pid, testDate);
      }
      await saveTestNote({ player_id: pid, test_date: testDate, notes, created_by: createdBy });

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setMobility(emptyMobility); setStrength(emptyStrength);
      setSprint({ sprint_5m: '', sprint_10m: '', sprint_30m: '' });
      setCmj({ height_cm: '', peak_power_wkg: '' });
      setHop({ mean_rsi: '' });
      setDropjump({ rsi: '', peak_power_wkg: '' });
      setDropjumpSL({ rsi_left: '', rsi_right: '', peak_power_wkg_left: '', peak_power_wkg_right: '' });
      setIsrt({ final_stage: '', hr_at_end: '', hr_recovery_30s: '', hr_recovery_60s: '', hr_recovery_90s: '', hr_recovery_120s: '' });
      setWeight({ weight_kg: '' });
      setSkinfold(emptySkinfold);
      setNotes('');
      setExistingForDate({});
    } catch (err) {
      console.error('Failed to save tests:', err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (saved) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
      <CheckCircle size={64} color="#10b981" strokeWidth={1.5} />
      <h2 style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>Test results saved!</h2>
    </div>
  );

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Physical Test Input</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Enter results for any tests completed today. Partial saves are fine — not every test needs doing on the same day.</p>
      </div>

      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', marginBottom: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Player</label>
          <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={inputStyle}>
            <option value="">Select player...</option>
            {players.filter(p => p.active).sort((a, b) => a.name.localeCompare(b.name)).map(p => (
              <option key={p.id} value={p.id}>{p.name} {p.squad_number ? `(#${p.squad_number})` : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Test date</label>
          <input type="date" value={testDate} onChange={e => setTestDate(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <CollapsibleSection title="Mobility" open={open.mobility} onToggle={() => toggle('mobility')}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 80px', gap: 10, fontSize: 11, color: 'var(--text-muted)', padding: '4px 0', fontWeight: 700 }}>
          <div>Test</div><div>Left</div><div>Right</div><div style={{ textAlign: 'center' }}>LSI %</div>
        </div>
        {MOBILITY_TESTS.map(t => (
          <LRRow key={t.key} label={t.label}
            left={mobility[`${t.key}_left`]} right={mobility[`${t.key}_right`]}
            onLeft={v => setM(t.key, 'left', v)} onRight={v => setM(t.key, 'right', v)} />
        ))}
      </CollapsibleSection>

      <CollapsibleSection title="Strength" open={open.strength} onToggle={() => toggle('strength')}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 80px', gap: 10, fontSize: 11, color: 'var(--text-muted)', padding: '4px 0', fontWeight: 700 }}>
          <div>Test</div><div>Left</div><div>Right</div><div style={{ textAlign: 'center' }}>LSI %</div>
        </div>
        {STRENGTH_TESTS.map(t => (
          <LRRow key={t.key} label={t.label}
            left={strength[`${t.key}_left`]} right={strength[`${t.key}_right`]}
            onLeft={v => setS(t.key, 'left', v)} onRight={v => setS(t.key, 'right', v)} />
        ))}
        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 8, padding: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ratio H:Q</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: hqColor(hq) }}>{hq !== null ? hq.toFixed(2) : '—'}</div>
          </div>
          <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 8, padding: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ratio AB:AD</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: abAdColor(abad) }}>{abad !== null ? `${abad.toFixed(0)}%` : '—'}</div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Performance Tests" open={open.performance} onToggle={() => toggle('performance')}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Sprint</div>
          <div style={{ display: 'grid', gridTemplateColumns: isGK ? '1fr 1fr 1fr' : '1fr 1fr', gap: 10 }}>
            {isGK && (
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>5m (s)</label>
                <input type="number" value={sprint.sprint_5m} onChange={e => setSprint(f => ({ ...f, sprint_5m: e.target.value }))} style={inputStyle} />
              </div>
            )}
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>10m (s)</label>
              <input type="number" value={sprint.sprint_10m} onChange={e => setSprint(f => ({ ...f, sprint_10m: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>30m (s)</label>
              <input type="number" value={sprint.sprint_30m} onChange={e => setSprint(f => ({ ...f, sprint_30m: e.target.value }))} style={inputStyle} />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>CMJ</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Height (cm)</label>
              <input type="number" value={cmj.height_cm} onChange={e => setCmj(f => ({ ...f, height_cm: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Peak power (W/kg)</label>
              <input type="number" value={cmj.peak_power_wkg} onChange={e => setCmj(f => ({ ...f, peak_power_wkg: e.target.value }))} style={inputStyle} />
              {player?.weight && cmj.peak_power_wkg !== '' && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  = {cmjPeakPowerWatts(cmj.peak_power_wkg, player.weight)?.toFixed(0)} W (at {player.weight}kg)
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Hop test</div>
          <div style={{ width: '50%' }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Mean RSI</label>
            <input type="number" value={hop.mean_rsi} onChange={e => setHop({ mean_rsi: e.target.value })} style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Drop Jump</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>RSI</label>
              <input type="number" value={dropjump.rsi} onChange={e => setDropjump(f => ({ ...f, rsi: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Peak power (W/kg)</label>
              <input type="number" value={dropjump.peak_power_wkg} onChange={e => setDropjump(f => ({ ...f, peak_power_wkg: e.target.value }))} style={inputStyle} />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Single Leg Drop Jump</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 80px', gap: 10, fontSize: 11, color: 'var(--text-muted)', padding: '4px 0', fontWeight: 700 }}>
            <div>Test</div><div>Left</div><div>Right</div><div style={{ textAlign: 'center' }}>LSI %</div>
          </div>
          <LRRow label="RSI"
            left={dropjumpSL.rsi_left} right={dropjumpSL.rsi_right}
            onLeft={v => setDropjumpSL(f => ({ ...f, rsi_left: v }))} onRight={v => setDropjumpSL(f => ({ ...f, rsi_right: v }))} />
          <LRRow label="Peak power (W/kg)"
            left={dropjumpSL.peak_power_wkg_left} right={dropjumpSL.peak_power_wkg_right}
            onLeft={v => setDropjumpSL(f => ({ ...f, peak_power_wkg_left: v }))} onRight={v => setDropjumpSL(f => ({ ...f, peak_power_wkg_right: v }))} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>ISRT MAX</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Final stage</label>
              <input type="number" value={isrt.final_stage} onChange={e => setIsrt(f => ({ ...f, final_stage: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>HR at end</label>
              <input type="number" value={isrt.hr_at_end} onChange={e => setIsrt(f => ({ ...f, hr_at_end: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>HR rec. 30s</label>
              <input type="number" value={isrt.hr_recovery_30s} onChange={e => setIsrt(f => ({ ...f, hr_recovery_30s: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>HR rec. 60s</label>
              <input type="number" value={isrt.hr_recovery_60s} onChange={e => setIsrt(f => ({ ...f, hr_recovery_60s: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>HR rec. 90s</label>
              <input type="number" value={isrt.hr_recovery_90s} onChange={e => setIsrt(f => ({ ...f, hr_recovery_90s: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div style={{ width: 'calc(20% - 8px)', marginTop: 10 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>HR rec. 120s</label>
            <input type="number" value={isrt.hr_recovery_120s} onChange={e => setIsrt(f => ({ ...f, hr_recovery_120s: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={handleIsrtHrSync} disabled={isrtSyncing}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: isrtSyncing ? 'default' : 'pointer' }}>
              <RefreshCw size={14} style={{ animation: isrtSyncing ? 'spin 1s linear infinite' : 'none' }} /> Fill HR at end from GPS
            </button>
            {isrtSyncMsg && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isrtSyncMsg}</span>}
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 6 }}>
            Pulls the peak HR from that day's GPS session's 'ISRT Max' drill for any test still missing "HR at end" — never overwrites a value already entered.
          </p>
        </div>

        <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Passing Drill HR Test</div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            Auto-detected from GPS uploads tagged 'Pas en trap-HR Test'.
          </p>
          <button onClick={handleSync} disabled={syncing}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: syncing ? 'default' : 'pointer' }}>
            <RefreshCw size={14} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} /> Sync now
          </button>
          {syncMsg && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{syncMsg}</div>}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Other Tests" open={open.other} onToggle={() => toggle('other')}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Weight</div>
          <div style={{ width: '50%' }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Weight (kg)</label>
            <input type="number" value={weight.weight_kg} onChange={e => setWeight({ weight_kg: e.target.value })} style={inputStyle} />
          </div>
        </div>

        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Anthropometry</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 70px 1fr 80px', gap: 10, fontSize: 11, color: 'var(--text-muted)', padding: '4px 0', fontWeight: 700 }}>
            <div>Site</div><div>M1</div><div>M2</div><div>Diff %</div><div>M3</div><div style={{ textAlign: 'center' }}>Final</div>
          </div>
          {skinfoldRows.map(row => (
            <div key={row.site} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 70px 1fr 80px', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-strong)' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{row.site}</div>
              <input type="number" value={row.m1} onChange={e => setSkinfold(f => ({ ...f, [row.site]: { ...f[row.site], m1: e.target.value } }))} style={inputStyle} />
              <input type="number" value={row.m2} onChange={e => setSkinfold(f => ({ ...f, [row.site]: { ...f[row.site], m2: e.target.value } }))} style={inputStyle} />
              <div style={{ fontSize: 12, color: row.diff !== null && row.diff > 10 ? '#ef4444' : 'var(--text-muted)' }}>
                {row.diff !== null ? `${row.diff.toFixed(1)}%` : '—'}
              </div>
              {row.needsM3 ? (
                <input type="number" value={row.m3} onChange={e => setSkinfold(f => ({ ...f, [row.site]: { ...f[row.site], m3: e.target.value } }))} style={inputStyle} placeholder="M3" />
              ) : <div />}
              <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'center' }}>{row.finalVal !== null ? row.finalVal.toFixed(1) : '—'}</div>
            </div>
          ))}
          <div style={{ marginTop: 14, background: 'var(--bg-app)', borderRadius: 8, padding: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sum of 8</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{sum8Complete ? sum8.toFixed(1) : `${sum8.toFixed(1)} (partial)`}</div>
          </div>
        </div>
      </CollapsibleSection>

      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Notes</div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Any extra information for this test session — e.g. player reported tightness, test conditions, things to follow up on…"
          style={{ ...inputStyle, minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      <button onClick={handleSaveAll} disabled={saving}
        style={{ width: '100%', marginTop: 8, padding: '14px', borderRadius: 10, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
        {saving ? 'Saving...' : 'Save all'}
      </button>
    </div>
  );
}
